import { excludeInternalSupportEmployees } from '../../../utils/employeeVisibility';
import React, { useState, useEffect } from "react";
import { Autocomplete, TextField, Box } from '@mui/material';
import ReportToolbar from "../ReportToolbar";
import ReportFooter from "../ReportFooter";
import { ProgramFilter, ReportFilterBar } from "../ReportFilters";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers-pro/AdapterDayjs";
import { DateRangePicker } from "@mui/x-date-pickers-pro/DateRangePicker";
import { DataGridPro } from '@mui/x-data-grid-pro';
import moment from "moment";
import axios from "axios";
import { APIURL } from "../../../config";
import dayjs from "dayjs";

const shortcutsItems = [
    {
        label: 'This Week',
        getValue: () => {
            const today = dayjs();
            return [today.startOf('week'), today.endOf('week')];
        },
    },
    {
        label: 'Last Week',
        getValue: () => {
            const today = dayjs();
            const prevWeek = today.subtract(7, 'day');
            return [prevWeek.startOf('week'), prevWeek.endOf('week')];
        },
    },
    {
        label: 'Last 7 Days',
        getValue: () => {
            const today = dayjs();
            return [today.subtract(7, 'day'), today];
        },
    },
    {
        label: 'Current Month',
        getValue: () => {
            const today = dayjs();
            return [today.startOf('month'), today.endOf('month')];
        },
    },
    {
        label: 'Last Month',
        getValue: () => {
            const today = dayjs();
            const lastMonth = today.subtract(1, 'month');
            return [lastMonth.startOf('month'), lastMonth.endOf('month')];
        },
    },
];

export default function HistoricalVisitsSummaryReport(props) {
    const [isLoading, setIsLoading] = useState(true);
    const [data, setData] = useState([]);
    const [customerList, setCustomerList] = useState([]);
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [employeeList, setEmployeeList] = useState([]);
    const [selectedEmployee, setSelectedEmployee] = useState(null);
    const [selectedProgram, setSelectedProgram] = useState(null);
    const { startDate, endDate, setStartDate, setEndDate } = props;

    useEffect(() => {
        fetchData();
        fetchEmployeeData();
    }, []);

    const fetchData = async () => {
        setIsLoading(true);

        axios
            .get(APIURL + "/reports/getHistoricalVisitsSummaryReport", {
                params: {
                    compID: props.user.comp_id,
                    empID: props.user.emp_id,
                    empType: props.user.type,
                    selectedEmpID: selectedEmployee,
                    custID: selectedCustomer?.cus_id,
                    start: moment(startDate).startOf("day").format("YYYY-MM-DDTHH:mm:ss"),
                    end: moment(endDate).endOf("day").format("YYYY-MM-DDTHH:mm:ss"),
                    program_id: selectedProgram || undefined,
                },
            })
            .then((response) => {
                setData(response?.data.length !== 0 ? response?.data : []);
            })
            .catch((err) => {
                console.log(err);
            })
            .finally(() => {
                setIsLoading(false);
            });

        axios
            .get(APIURL + "/customers/v2/associated", {
                params: {
                    company_id: props.user.comp_id,
                    employee_id: props.user.emp_id,
                    type: props.user.permissions.AdminLevelCustomers ? "Admin" : "Manager"
                },
            })
            .then((response) => {
                setCustomerList(response?.data.filter((cus) => cus.cus_status === 'Active') || []);
            })
            .catch((err) => {
                console.log(err);
            });
    };

    const fetchEmployeeData = async () => {
        if (props.user.type === 'Admin') {
            axios
                .get(APIURL + '/dropdowns/dropdownType/employeeslist', {
                    params: {
                        comp_id: props.user.comp_id
                    }
                })
                .then((response) => {
                    const empList = excludeInternalSupportEmployees(response.data).map((emp) => { return { emp_id: emp.id, emp_name: emp.employee_name } });
                    setEmployeeList(empList);
                });
        } else if (props.user.type.includes('Manager')) {
            axios
                .get(APIURL + "/employees/v2/associated", {
                    params: {
                        company_id: props.user.comp_id,
                        employee_id: props.user.emp_id
                    },
                })
                .then((response) => {
                    let associatedEmployees = response.data.directlyManaged.concat(response.data.hr_managed).concat({ emp_id: props.user.emp_id, emp_name: props.user.emp_name, emp_is_internal_support: props.user.emp_is_internal_support });

                    associatedEmployees = excludeInternalSupportEmployees(associatedEmployees).filter((obj, index, self) => {
                        return index === self.findIndex((t) => (
                            t.emp_id === obj.emp_id
                        ));
                    }).map((emp) => { return { emp_id: emp.emp_id, emp_name: emp.emp_name ? emp.emp_name : emp.emp_first_name + ' ' + emp.emp_last_name } });

                    setEmployeeList(associatedEmployees);
                });
        } else {
            setEmployeeList(excludeInternalSupportEmployees([{ emp_id: props.user.emp_id, emp_name: props.user.emp_name, emp_is_internal_support: props.user.emp_is_internal_support }]));
        }
    };

    // Group flat API rows by schedule. Each schedule becomes a parent row with
    // the shared shift details; each shift_action_log entry becomes a child row.
    const rows = (() => {
        const schedules = new Map();

        data.forEach((item) => {
            if (!schedules.has(item.schedule_id)) {
                schedules.set(item.schedule_id, {
                    schedule_id: item.schedule_id,
                    schedule_name: item.schedule_name || `Schedule ${item.schedule_id}`,
                    task_type: item.task_type || '',
                    emp_name: item.emp_first_name + ' ' + item.emp_last_name,
                    cus_name: item.cus_name || '',
                    check_in_time: item.empLocdate ? moment(item.empLocdate).format('MM/DD/YYYY hh:mm A') : '',
                    check_out_time: item.checkoutEmpDate ? moment(item.checkoutEmpDate).format('MM/DD/YYYY hh:mm A') : '',
                    actions: [],
                });
            }
            if (item.action) {
                schedules.get(item.schedule_id).actions.push({
                    action: item.action,
                    action_notes: item.action_notes || '',
                    action_at: item.action_at ? moment(item.action_at).format('MM/DD/YYYY hh:mm A') : '',
                    action_by_name: item.action_by_first_name ? item.action_by_first_name + ' ' + item.action_by_last_name : '',
                });
            }
        });

        const result = [];
        schedules.forEach((sched) => {
            // Parent row: shared shift fields
            result.push({
                id: `s-${sched.schedule_id}`,
                path: [sched.schedule_name],
                emp_name: sched.emp_name,
                cus_name: sched.cus_name,
                task_type: sched.task_type,
                check_in_time: sched.check_in_time,
                check_out_time: sched.check_out_time,
                action: '',
                action_notes: '',
                action_by_name: '',
                action_at: '',
            });
            // Child rows: one per action log entry
            sched.actions.forEach((a, i) => {
                result.push({
                    id: `s-${sched.schedule_id}-a-${i}`,
                    path: [sched.schedule_name, `${sched.schedule_id}-${i}`],
                    emp_name: '',
                    cus_name: '',
                    task_type: '',
                    check_in_time: '',
                    check_out_time: '',
                    action: a.action,
                    action_notes: a.action_notes,
                    action_by_name: a.action_by_name,
                    action_at: a.action_at,
                });
            });
        });
        return result;
    })();

    const columns = [
        { field: "emp_name", headerName: "Employee", width: 200 },
        { field: "cus_name", headerName: "Client", width: 200 },
        { field: "task_type", headerName: "Shift Type", width: 130 },
        { field: "check_in_time", headerName: "Check In", width: 120 },
        { field: "check_out_time", headerName: "Check Out", width: 120 },
        { field: "action", headerName: "Action", width: 180 },
        { field: "action_notes", headerName: "Action Notes", width: 250 },
        { field: "action_by_name", headerName: "Action By", width: 180 },
        { field: "action_at", headerName: "Action Time", width: 170 },
    ];

    const CustomToolbar = () => {
        return (
            <ReportToolbar companyName={props.user.company_name} reportTitle="Historical Visits Summary">
                <Box sx={{ pl: 1, pr: 1 }}>
                    <div style={{ fontSize: '16px', fontWeight: 'bold' }}>
                        Date Range: {moment(startDate).format('MM/DD/YYYY')} - {moment(endDate).format('MM/DD/YYYY')}
                    </div>
                </Box>
            </ReportToolbar>
        );
    };

    return (
        <div style={{ width: '100%', padding: 10 }}>
            <div style={{ display: 'flex', marginBottom: 10, justifyContent: 'space-between' }}>
                <ReportFilterBar onSearch={() => fetchData()} style={{ flexWrap: 'wrap', rowGap: 10 }}>
                    <LocalizationProvider
                        dateAdapter={AdapterDayjs}
                        localeText={{ start: "From Date", end: "To Date" }}
                    >
                        <DateRangePicker
                            label='Date Range'
                            value={[
                                startDate ? dayjs(startDate) : null,
                                endDate ? dayjs(endDate) : null,
                            ]}
                            onChange={(newValue) => {
                                setStartDate(newValue[0] ? new Date(newValue[0]) : null);
                                setEndDate(newValue[1] ? new Date(newValue[1]) : null);
                            }}
                            slotProps={{
                                shortcuts: {
                                    items: shortcutsItems,
                                },
                                textField: { size: 'small', style: { width: 275 } }
                            }}
                        />
                    </LocalizationProvider>

                    <Autocomplete
                        id="employee-select"
                        options={employeeList}
                        getOptionLabel={(option) => option.emp_name}
                        onChange={(event, value) => setSelectedEmployee(value?.emp_id ? value.emp_id : null)}
                        renderOption={(props, option) => (
                            <li {...props} key={option.emp_id}>
                                {option.emp_name}
                            </li>
                        )}
                        renderInput={(params) => <TextField {...params} label="Select Employee" />}
                        sx={{ width: 250 }}
                        size="small"
                    />

                    <Autocomplete
                        id="client-select"
                        options={customerList}
                        getOptionLabel={(option) => option.cus_name}
                        onChange={(event, value) => setSelectedCustomer(value)}
                        renderOption={(props, option) => (
                            <li {...props} key={option.cus_id}>
                                {option.cus_name}
                            </li>
                        )}
                        renderInput={(params) => <TextField {...params} label="Select Client" />}
                        sx={{ width: 250 }}
                        size="small"
                    />

                    <ProgramFilter
                        comp_id={props.user.comp_id}
                        value={selectedProgram}
                        onChange={setSelectedProgram}
                    />
                </ReportFilterBar>
            </div>
            <div style={{ height: 'calc(100vh - 250px)' }}>
                <DataGridPro
                    getRowId={(row) => row.id}
                    rows={rows}
                    columns={columns}
                    checkboxSelection={false}
                    disableRowSelectionOnClick
                    showToolbar
                    slots={{
                        toolbar: CustomToolbar,
                        footer: ReportFooter
                    }}
                    loading={isLoading}
                    treeData
                    getTreeDataPath={(row) => row.path}
                    groupingColDef={{ headerName: 'Schedule', width: 260 }}
                    sx={{
                        '& .MuiDataGrid-columnHeaderTitle': {
                            whiteSpace: 'normal',
                            lineHeight: 1.2,
                            textAlign: 'left',
                            wordBreak: 'break-word',
                            maxWidth: '100%',
                        },
                    }}
                />
            </div>
        </div>
    );
}
